import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { datasourceBillsApi, type DatasourceBill} from '@/api/datasource-bills-api'
import type { APISession } from '@/types/session'

interface DatasourceBillsReturn {
  bills: DatasourceBill[]
  isLoading: boolean
  isError: boolean
  error: string | null
  selectedDataSource: string
  selectedSession: APISession | null
  setSelectedDataSource: (dataSource: string) => void
  setSelectedSession: (session: APISession) => void
  currentPage: number
  pageSize: number
  totalPages: number
  handlePageChange: (page: number) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  totalFilteredItems: number
  refetch: () => void
  loadAllBills: () => void
  isLoadingAll: boolean
  allBillsLoaded: boolean
  allBillsCount: number
}

export const useDatasourceBills = (
  initialDataSource: string = 'Maryland',
  initialSession?: APISession
): DatasourceBillsReturn => {
  const [selectedDataSource, setSelectedDataSource] = useState<string>(initialDataSource)
  const [selectedSession, setSelectedSession] = useState<APISession | null>(
    initialSession || '2024RS'
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const pageSize = 50

  // Store pagination tokens for each page to enable forward/backward navigation
  const [pageTokens, setPageTokens] = useState<Map<number, string | undefined>>(new Map())
  const [totalFilteredItems, setTotalFilteredItems] = useState(0)

  // Load all bills state
  const [allBills, setAllBills] = useState<DatasourceBill[]>([])
  const [isLoadingAll, setIsLoadingAll] = useState(false)
  const [allBillsLoaded, setAllBillsLoaded] = useState(false)
  const [allBillsCount, setAllBillsCount] = useState(0)

  // Reset pagination state when filters change
  useEffect(() => {
    setCurrentPage(1)
    setPageTokens(new Map([[1, undefined]])) // Page 1 starts with no token
    setTotalFilteredItems(0)
    setAllBillsLoaded(false)
    setAllBills([])
    setAllBillsCount(0)
  }, [selectedDataSource, selectedSession, searchQuery])

  // Get the next_token for the current page
  const getCurrentPageToken = useCallback(() => {
    return pageTokens.get(currentPage)
  }, [pageTokens, currentPage])

  // Fetch datasource bills with server-side pagination
  const billsQuery = useQuery({
    queryKey: [
      'datasource-bills-paginated',
      selectedDataSource,
      selectedSession,
      currentPage,
      searchQuery
    ],
    queryFn: async () => {
      if (!selectedSession) {
        throw new Error('No session selected')
      }

      console.log('[useDatasourceBills] Fetching bills for page:', currentPage, {
        data_source: selectedDataSource,
        session_code: selectedSession,
        search: searchQuery
      })

      const nextToken = getCurrentPageToken()

      const response = await datasourceBillsApi.listDatasourceBills({
        data_source: selectedDataSource,
        session_code: selectedSession,
        limit: pageSize,
        next_token: nextToken,
        // TODO: Add search parameters when backend supports them
        // search_query: searchQuery,
      })

      console.log(`[useDatasourceBills] Page ${currentPage}: ${response.items.length} bills`)

      // Store the next page token if it exists
      if (response.next_token) {
        setPageTokens(prev => {
          const newTokens = new Map(prev)
          newTokens.set(currentPage + 1, response.next_token)
          return newTokens
        })
      }

      return response
    },
    enabled: !!selectedSession && !!selectedDataSource,
    refetchOnWindowFocus: false,
    staleTime: 60000, // 1 minute
    gcTime: 300000, // 5 minutes
  })

  // Load all bills function
  const loadAllBills = useCallback(async () => {
    if (!selectedSession || !selectedDataSource || isLoadingAll) {
      return
    }

    setIsLoadingAll(true)
    setAllBillsCount(0)
    setAllBills([]) // Clear existing bills

    try {
      console.log('[useDatasourceBills] Starting to load all bills')

      const allBillsData = await datasourceBillsApi.loadAllDatasourceBills({
        data_source: selectedDataSource,
        session_code: selectedSession,
        search_query: searchQuery.trim() || undefined,
        on_progress: (currentCount) => {
          setAllBillsCount(currentCount)
          // Don't update allBills progressively to avoid pagination issues
          // Just update the count for progress indication
        }
      })

      // Set the final result
      setAllBills(allBillsData)
      setAllBillsLoaded(true)
      setAllBillsCount(allBillsData.length)
      setCurrentPage(1) // Reset to first page when all bills are loaded
      console.log(`[useDatasourceBills] Successfully loaded ${allBillsData.length} bills`)

    } catch (error) {
      console.error('[useDatasourceBills] Failed to load all bills:', error)
      setAllBills([])
      setAllBillsLoaded(false)
      setAllBillsCount(0)
    } finally {
      setIsLoadingAll(false)
    }
  }, [selectedDataSource, selectedSession, searchQuery, isLoadingAll])

  // Apply client-side filtering until backend supports it
  const processedBills = useMemo(() => {
    // If all bills are loaded, use them instead of paginated results
    const sourceBills = allBillsLoaded ? allBills : (billsQuery.data?.items || [])

    // Apply client-side search filtering
    let filteredBills = sourceBills
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toUpperCase()
      filteredBills = sourceBills.filter(bill => {
        const bill_number = bill.bill_number || ''
        const title = bill.title || ''
        const synopsis = bill.synopsis || ''
        return bill_number.toUpperCase().includes(query) ||
               title.toUpperCase().includes(query) ||
               synopsis.toUpperCase().includes(query)
      })
    }

    return filteredBills
  }, [billsQuery.data?.items, searchQuery, allBills, allBillsLoaded])

  // Calculate total pages (this is approximate since we don't know the exact total)
  // We'll estimate based on whether there's a next_token
  const totalPages = useMemo(() => {
    // If all bills are loaded, calculate exact pages
    if (allBillsLoaded) {
      return Math.max(1, Math.ceil(processedBills.length / pageSize))
    }

    const hasNextPage = !!billsQuery.data?.next_token
    const currentPageHasBills = processedBills.length > 0

    if (!currentPageHasBills && currentPage === 1) {
      return 1 // At least one page, even if empty
    }

    if (hasNextPage) {
      return currentPage + 1 // At least one more page exists
    }

    return currentPage // This is the last page
  }, [billsQuery.data?.next_token, processedBills.length, currentPage, allBillsLoaded, pageSize])

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  // Get bills to pass to DataTable
  const getBillsForTable = useMemo(() => {
    if (!allBillsLoaded) {
      // Paginated mode: return current page data, DataTable will sort within this page
      return processedBills
    }

    // All loaded mode: return ALL bills, let DataTable handle sorting and pagination
    return processedBills
  }, [allBillsLoaded, processedBills])

  // Update total filtered items estimate
  useEffect(() => {
    if (allBillsLoaded) {
      setTotalFilteredItems(processedBills.length)
    } else if (processedBills.length > 0) {
      // This is an estimate - we don't know the true total with server-side pagination
      const estimate = (currentPage - 1) * pageSize + processedBills.length
      setTotalFilteredItems(estimate)
    }
  }, [processedBills.length, currentPage, pageSize, allBillsLoaded])

  return {
    bills: getBillsForTable,
    isLoading: billsQuery.isPending,
    isError: billsQuery.isError,
    error: billsQuery.isError ? 'Failed to load datasource bills' : null,
    selectedDataSource,
    selectedSession,
    setSelectedDataSource,
    setSelectedSession,
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    searchQuery,
    setSearchQuery,
    totalFilteredItems, // This is now an estimate or exact count if all loaded
    refetch: billsQuery.refetch,
    loadAllBills,
    isLoadingAll,
    allBillsLoaded,
    allBillsCount
  }
}
